// LocalStorage helpers — strictly client-side for privacy.
// Keys are namespaced under `un_*`.
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const KEYS = {
  JOURNAL: "un_journal_entries_v1",
  RESULTS: "un_quiz_results_v1",
  ACTIONS: "un_saved_actions_v1",
  QUIZ_DRAFT: "un_quiz_draft_v1",
  ONBOARDED: "un_onboarded_v1",
  MOSAIC: "un_mosaic_v1",
  MOMENTS: "un_moments_v1",
  PROTECT: "un_protect_items_v1",
  DRAFTS: "un_drafts_v1",
};

// Soft, transparent storage error surfacing — never silent.
// Tone: protective, human. Reinforces: "Your reflections belong to you."
let lastErrorAt = 0;
const isQuotaError = (e) => {
  if (!e) return false;
  if (e.name === "QuotaExceededError") return true;
  if (e.name === "NS_ERROR_DOM_QUOTA_REACHED") return true;
  if (e.code === 22 || e.code === 1014) return true;
  return /quota/i.test(String(e.message || ""));
};

const surfaceStorageError = (e) => {
  // Throttle: don't fire more than once every 4 seconds.
  const now = Date.now();
  if (now - lastErrorAt < 4000) return;
  lastErrorAt = now;
  if (isQuotaError(e)) {
    toast("Your reflections deserve a safe place to stay.", {
      description:
        "Your device may be running low on private space. Your reflections belong to you — try removing a photo from Mosaic, or clearing an older entry, to make room.",
      duration: 8000,
    });
  } else {
    toast("Couldn't save that just now.", {
      description:
        "Your reflections belong to you — please try again in a moment.",
      duration: 6000,
    });
  }
};

const read = (k, fallback) => {
  try {
    const raw = localStorage.getItem(k);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const write = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
    return true;
  } catch (e) {
    surfaceStorageError(e);
    return false;
  }
};

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// --- Journal ---
export const getJournalEntries = () => read(KEYS.JOURNAL, []);
export const addJournalEntry = ({ text, prompt }) => {
  const entries = getJournalEntries();
  const entry = {
    id: uid(),
    text: text.trim(),
    prompt: prompt || null,
    reflection: null,
    createdAt: new Date().toISOString(),
  };
  entries.unshift(entry);
  write(KEYS.JOURNAL, entries);
  track("reflection_completed", { mode: "journal" });
  return entry;
};
export const saveReflectionToEntry = (id, reflection) => {
  const entries = getJournalEntries().map((e) =>
    e.id === id ? { ...e, reflection } : e
  );
  write(KEYS.JOURNAL, entries);
  return entries.find((e) => e.id === id);
};
export const deleteJournalEntry = (id) => {
  const entries = getJournalEntries().filter((e) => e.id !== id);
  write(KEYS.JOURNAL, entries);
};
export const clearJournal = () => write(KEYS.JOURNAL, []);

// --- Quiz Results ---
export const getResults = () => read(KEYS.RESULTS, []);
export const saveResult = (result) => {
  const results = getResults();
  const entry = {
    id: uid(),
    ...result,
    createdAt: new Date().toISOString(),
  };
  results.unshift(entry);
  write(KEYS.RESULTS, results);
  return entry;
};
export const deleteResult = (id) => {
  write(
    KEYS.RESULTS,
    getResults().filter((r) => r.id !== id)
  );
};

// --- Saved Step Forward actions ---
export const getSavedActions = () => read(KEYS.ACTIONS, []);
export const toggleSavedAction = (action) => {
  const current = getSavedActions();
  const exists = current.find((a) => a.id === action.id);
  let next;
  if (exists) {
    next = current.filter((a) => a.id !== action.id);
  } else {
    next = [
      { ...action, savedAt: new Date().toISOString() },
      ...current,
    ];
  }
  write(KEYS.ACTIONS, next);
  return !exists;
};
export const isActionSaved = (id) =>
  getSavedActions().some((a) => a.id === id);
export const deleteSavedAction = (id) => {
  write(
    KEYS.ACTIONS,
    getSavedActions().filter((a) => a.id !== id)
  );
};

// --- Quiz draft (in-progress) ---
export const getQuizDraft = () => read(KEYS.QUIZ_DRAFT, null);
export const setQuizDraft = (draft) => write(KEYS.QUIZ_DRAFT, draft);
export const clearQuizDraft = () => write(KEYS.QUIZ_DRAFT, null);

// --- Onboarded flag ---
export const isOnboarded = () => read(KEYS.ONBOARDED, false);
export const setOnboarded = () => write(KEYS.ONBOARDED, true);

// --- Focus areas (onboarding personalization) ---
export const getFocusAreas = () => read("un_focus_v1", []);
export const setFocusAreas = (areas) => write("un_focus_v1", areas);

// --- Mosaic items now live encrypted in IndexedDB.
// See `@/lib/mosaicVault` for the public API. The legacy `un_mosaic_v1`
// localStorage key is migrated and cleared on first read.

// --- Moments worth honoring ---
export const getMoments = () => read(KEYS.MOMENTS, []);
export const logMoment = (type) => {
  const moments = getMoments();
  const entry = {
    id: uid(),
    type,
    createdAt: new Date().toISOString(),
  };
  moments.unshift(entry);
  write(KEYS.MOMENTS, moments);
  return entry;
};
export const deleteMoment = (id) => {
  write(
    KEYS.MOMENTS,
    getMoments().filter((m) => m.id !== id)
  );
};
export const getMomentCounts = () => {
  const counts = {};
  getMoments().forEach((m) => {
    counts[m.type] = (counts[m.type] || 0) + 1;
  });
  return counts;
};

// --- Protect List (what the user is protecting) ---
export const getProtectItems = () => read(KEYS.PROTECT, []);
export const addProtectItem = ({ category, label }) => {
  const items = getProtectItems();
  const entry = {
    id: uid(),
    category,
    label: label.trim(),
    createdAt: new Date().toISOString(),
  };
  items.unshift(entry);
  write(KEYS.PROTECT, items);
  return entry;
};
export const deleteProtectItem = (id) => {
  write(
    KEYS.PROTECT,
    getProtectItems().filter((i) => i.id !== id)
  );
};

// --- Drafts ("Held until morning") ---
// status: 'held' | 'released' | 'sent'
const nextMorning = (from = new Date()) => {
  const d = new Date(from);
  d.setHours(6, 0, 0, 0);
  if (d <= from) d.setDate(d.getDate() + 1);
  return d.toISOString();
};

export const getDrafts = () => read(KEYS.DRAFTS, []);
export const addDraft = ({ text, recipient }) => {
  const drafts = getDrafts();
  const entry = {
    id: uid(),
    text: text.trim(),
    recipient: recipient ? recipient.trim() : null,
    createdAt: new Date().toISOString(),
    surfaceAfter: nextMorning(),
    status: "held",
    decidedAt: null,
  };
  drafts.unshift(entry);
  write(KEYS.DRAFTS, drafts);
  return entry;
};
export const updateDraft = (id, patch) => {
  const drafts = getDrafts().map((d) =>
    d.id === id ? { ...d, ...patch } : d
  );
  write(KEYS.DRAFTS, drafts);
  return drafts.find((d) => d.id === id);
};
export const deleteDraft = (id) => {
  write(
    KEYS.DRAFTS,
    getDrafts().filter((d) => d.id !== id)
  );
};
export const holdAgain = (id) =>
  updateDraft(id, {
    status: "held",
    surfaceAfter: nextMorning(),
    decidedAt: null,
  });
export const markSent = (id) =>
  updateDraft(id, { status: "sent", decidedAt: new Date().toISOString() });
export const releaseDraft = (id) =>
  updateDraft(id, {
    status: "released",
    decidedAt: new Date().toISOString(),
  });

// Drafts that have surfaced (now past their morning timestamp) and remain held.
export const getResurfacedDrafts = () => {
  const now = Date.now();
  return getDrafts().filter(
    (d) => d.status === "held" && new Date(d.surfaceAfter).getTime() <= now
  );
};

// --- Clear everything ---
export const clearAll = () => {
  [...Object.values(KEYS), "un_streaks_v1", "un_safe_mode_v1", "un_peace_compass_v1", "un_future_funds_v1", "un_alignment_v1", "un_focus_v1"].forEach((k) => localStorage.removeItem(k));
};

export const STORAGE_KEYS = KEYS;
